import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Badge,
    Pagination, PaginationItem, PaginationLink, Label, Input
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import axios from "axios";
import { getCookie } from "../../Helpers/Utils";

// MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const CreditNoteList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [creditNotes, setCreditNotes] = useState([]);
    const [summary, setSummary] = useState({
        total_notes: 0,
        total_credit_subtotal: 0, // Name updated to match API
        total_credit_gst: 0,
        total_credit_with_gst: 0,
        total_reduced_kg: 0,
        total_reduced_quintal: 0
    });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0 });

    const [filters, setFilters] = useState({
        from_date: null,
        to_date: null,
        credit_note_no: "",
        type: null,
        page: 1,
        limit: 10
    });

    const typeOptions = [
        { value: "amount", label: "By Amount" },
        { value: "kg", label: "By Weight" }
    ];

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '45px',
            height: '45px',
            borderRadius: '8px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
        valueContainer: (provided) => ({ ...provided, height: '45px', padding: '0 6px' }),
        input: (provided) => ({ ...provided, margin: '0px' }),
        indicatorSeparator: () => ({ display: 'none' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '45px' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        loadCreditNotes();
    }, [filters.page]);

    const loadCreditNotes = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                page: filters.page,
                limit: filters.limit,
                credit_note_no: filters.credit_note_no,
                type: filters.type ? filters.type.value : "",
                from_date: filters.from_date ? dayjs(filters.from_date).format('YYYY-MM-DD') : "",
                to_date: filters.to_date ? dayjs(filters.to_date).format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/credit_note/list.php?${queryParams}` });

            if (res && res.success) {
                setCreditNotes(res.data || []);
                // API summary structure update panniyachu machan
                setSummary({
                    total_notes: res.summary?.total_notes || 0,
                    total_credit_subtotal: res.summary?.total_credit_subtotal || 0,
                    total_credit_gst: res.summary?.total_credit_gst || 0,
                    total_credit_with_gst: res.summary?.total_credit_with_gst || 0,
                    total_reduced_kg: res.summary?.total_reduced_kg || 0,
                    total_reduced_quintal: res.summary?.total_reduced_quintal || 0
                });

                setPagination({
                    current_page: res.pagination?.current_page || 1,
                    total_pages: res.pagination?.total_pages || 1,
                    total_records: res.pagination?.total_records || 0
                });
            } else {
                toast.error(res?.message || "Failed to fetch credit notes");
            }
        } catch (err) {
            console.error("Frontend Error:", err);
            toast.error("Data loading error. Check console for details.");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const handleGeneratePDF = async (id) => {
        if (!id) return;
        dispatch(loadingTrue());
        const token = getCookie('token');

        try {
            const response = await axios({
                url: `${'https://muthaiah-cotton.qads.co.in/cotton-mill'}/credit_note/generate.php?id=${id}`,
                // url: `${'http://localhost/cotton-mill'}/credit_note/generate.php?id=${id}`,
                method: 'GET',
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/pdf'
                }
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(blob);
            window.open(fileURL, '_blank');

        } catch (err) {
            toast.error("Could not generate Credit Note PDF.");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1 bg-light min-vh-100">
                <div className="page-header mb-4 d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold m-0 text-danger">Credit Note History</h3>
                        <p className="text-muted small m-0">Tracking all bill reductions and weight returns.</p>
                    </div>
                    <Button color="danger" className="px-4 shadow-sm fw-bold" onClick={() => navigate("/creditnotes/add")}>
                        <i className="bx bx-plus-circle me-1"></i> New Credit Note
                    </Button>
                </div>

                {/* SUMMARY CARDS - SYNCED WITH API */}
                <Row className="mb-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-danger text-white">
                            <CardBody className="p-3 text-center">
                                <small className="opacity-75 text-uppercase fw-bold" style={{ fontSize: '10px' }}>Total Credit (Incl. GST)</small>
                                <h4 className="fw-bold mb-0">₹{summary.total_credit_with_gst?.toLocaleString()}</h4>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white border-start border-primary border-4">
                            <CardBody className="p-3 text-center">
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '10px' }}>Total GST Amount</small>
                                <h4 className="fw-bold mb-0 text-primary">₹{summary.total_credit_gst?.toLocaleString()}</h4>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white border-start border-warning border-4">
                            <CardBody className="p-3 text-center">
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '10px' }}>Total Weight Reduced</small>
                                <h4 className="fw-bold mb-0 text-warning">{summary.total_reduced_kg?.toLocaleString()} KG</h4>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white border-start border-info border-4">
                            <CardBody className="p-3 text-center">
                                <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '10px' }}>Reduced Quintals</small>
                                <h4 className="fw-bold mb-0 text-info">{summary.total_reduced_quintal.toFixed(2)} Qtl</h4>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* FILTERS */}
                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="p-4">
                        <Row className="g-2 align-items-end">
                            <Col md={2}>
                                <Label className="small fw-bold">From Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.from_date}
                                    onChange={(val) => setFilters({ ...filters, from_date: val, page: 1 })}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">To Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.to_date}
                                    onChange={(val) => setFilters({ ...filters, to_date: val, page: 1 })}
                                    minDate={filters.from_date}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={3}>
                                <Label className="small fw-bold">CN Number</Label>
                                <Input style={{ height: '45px' }} placeholder="Search CN No..." value={filters.credit_note_no} onChange={(e) => setFilters({ ...filters, credit_note_no: e.target.value, page: 1 })} />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">Type</Label>
                                <Select
                                    options={typeOptions}
                                    value={filters.type}
                                    onChange={(opt) => setFilters({ ...filters, type: opt, page: 1 })}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="All Types"
                                />
                            </Col>
                            <Col md={3}>
                                <Button color="primary" block className="fw-bold" style={{ height: '45px' }} onClick={() => { setFilters({ ...filters, page: 1 }); loadCreditNotes(); }}>
                                    <i className="bx bx-filter-alt"></i> Apply Filter
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* TABLE */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4">S.No</th>
                                    <th className="ps-4">CN Details</th>
                                    <th>Linked Invoice</th>
                                    <th>Reason</th>
                                    <th className="text-center">Reduction Qty</th>
                                    <th className="text-center">Credit Amount (Base + GST)</th>
                                    <th className="text-center pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {creditNotes.length > 0 ? (
                                    creditNotes.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="ps-4 text-muted">{(filters.page - 1) * filters.limit + index + 1}</td>
                                            <td className="ps-4">
                                                <div className="fw-bold text-dark">{item.credit_note_no}</div>
                                                <small className="text-muted">{dayjs(item.date).format('DD-MM-YYYY')}</small>
                                            </td>
                                            <td>
                                                <Badge color="light" className="text-danger border fw-bold mb-1">
                                                    <i className="bx bx-file me-1"></i>{item.invoice?.invoice_no || 'N/A'}
                                                </Badge>
                                                <div className="x-small text-muted">
                                                    Rem: ₹{item.invoice_current_status?.remaining_total_amount?.toLocaleString()}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small text-muted text-wrap" style={{ maxWidth: '200px' }}>
                                                    {item.reason || '---'}
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="fw-bold text-warning">-{item.credit.reduced_kg} KG</div>
                                                <small className="text-muted">{item.credit.reduced_quintal} Qtl</small>
                                            </td>
                                            <td className="text-center">
                                                <div className="fw-bold text-danger fs-6">₹{item.credit.total_credit_amount?.toLocaleString()}</div>
                                                <div className="x-small text-muted">
                                                    (₹{item.credit.base_amount} + ₹{item.credit.gst_amount} GST)
                                                </div>
                                                <Badge color="light" className="text-muted x-small text-uppercase mt-1" style={{ fontSize: '9px' }}>{item.type}</Badge>
                                            </td>
                                            <td className="text-center pe-4">
                                                <div className="d-flex justify-content-center gap-1">
                                                    <Button color="white" size="sm" className="shadow-sm border rounded-3" title="PDF" onClick={() => handleGeneratePDF(item.id)}>
                                                        <i className="bx bxs-file-pdf text-danger fs-5"></i>
                                                    </Button>
                                                    <Button color="white" size="sm" className="shadow-sm border rounded-3" title="Edit" onClick={() => navigate(`/creditnotes/${item.id}`)}>
                                                        <i className="bx bxs-edit text-primary fs-5"></i>
                                                    </Button>
                                                    <Button color="white" size="sm" className="shadow-sm border rounded-3" title="View" onClick={() => navigate(`/creditnotes-view/${item.id}`)}>
                                                        <i className="bx bx-show text-info fs-5"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="7" className="py-5 text-center text-muted">No credit notes found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* PAGINATION */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4 px-2">
                        <small className="text-muted">Showing {creditNotes.length} of {pagination.total_records} records</small>

                        <Pagination className="pagination-rounded shadow-sm mb-0">
                            {/* Previous Button */}
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>

                            {/* Dynamic Page Rendering */}
                            {[...Array(pagination?.total_pages)].map((_, i) => {
                                const pageNumber = i + 1;
                                const isCurrent = pageNumber === filters.page;

                                const shouldShow =
                                    pageNumber === 1 ||
                                    pageNumber === pagination?.total_pages ||
                                    (pageNumber >= filters.page - 1 && pageNumber <= filters.page + 1);

                                if (shouldShow) {
                                    return (
                                        <PaginationItem active={isCurrent} key={i}>
                                            <PaginationLink onClick={() => setFilters({ ...filters, page: pageNumber })}>
                                                {pageNumber}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                } else if (pageNumber === filters.page - 2 || pageNumber === filters.page + 2) {
                                    return (
                                        <PaginationItem disabled key={i}>
                                            <PaginationLink>...</PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            {/* Next Button */}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default CreditNoteList;